import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  Sparkle,
  Lightning,
  ArrowCounterClockwise,
  Printer,
  WarningCircle,
} from "@phosphor-icons/react";
import { getToolByKey, TOOLS } from "../lib/tools";
import { renderMarkdown } from "../lib/markdown";
import {
  AdsRenderer,
  SocialRenderer,
  ReviewsRenderer,
} from "../components/tools/ToolRenderers";
import PlanGate, { UsageCapBanner } from "../components/PlanGate";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_KEY_ALIASES = {
  lifecycle: "relate",
};

function normalizeToolKey(key) {
  const normalized = String(key || "").trim().toLowerCase();
  return TOOL_KEY_ALIASES[normalized] || normalized;
}

function sameToolKey(a, b) {
  return normalizeToolKey(a) === normalizeToolKey(b);
}

function getHistoryLookupKeys(toolKey) {
  const normalized = normalizeToolKey(toolKey);

  if (normalized === "relate") {
    return ["relate", "lifecycle"];
  }

  return [normalized];
}

function getApiErrorMessage(err) {
  const data = err?.response?.data;

  if (!data) {
    return "Could not connect to the AI service. Please try again.";
  }

  if (typeof data.error === "string") {
    return data.error;
  }

  if (typeof data.detail === "string") {
    return data.detail;
  }

  if (data.detail?.message) {
    return data.detail.message;
  }

  if (data.message) {
    return data.message;
  }

  return "Generation failed. Please try again.";
}

function normalizeGenerateResponse(data, toolKey, submittedInputs = {}) {
  const output =
    data?.output ||
    data?.result ||
    data?.content ||
    data?.text ||
    "";

  return {
    id: data?.id || data?.history_id || `local_${Date.now()}`,
    tool: normalizeToolKey(data?.tool || toolKey),
    raw_tool: data?.tool || toolKey,
    inputs:
      data?.inputs && typeof data.inputs === "object"
        ? data.inputs
        : submittedInputs,
    output,
    usage_source: data?.usage_source || null,
    addon_credit_used: Boolean(data?.addon_credit_used),
    created_at: data?.created_at || new Date().toISOString(),
  };
}

function hasValue(value) {
  return String(value || "").trim() !== "";
}

function getPdfDocumentTitle(toolName) {
  return `Ordeneli · Marketing AI Studio - ${toolName}`;
}

function normalizePlan(plan) {
  return String(plan || "starter").trim().toLowerCase();
}

function normalizeToolList(list) {
  return Array.isArray(list)
    ? list.map((item) => normalizeToolKey(item))
    : [];
}

function planUnlocksPaidTools(plan) {
  const normalized = normalizePlan(plan);

  return ["growth", "pro", "company", "agency", "enterprise"].includes(
    normalized
  );
}

function userCanAccessTool(tool, usage) {
  if (!tool) return false;
  if (tool.free) return true;

  if (!usage) {
    return true;
  }

  const plan = normalizePlan(usage.plan);
  if (planUnlocksPaidTools(plan)) return true;

  const allowedTools = normalizeToolList(usage.tools_allowed);
  return allowedTools.includes(normalizeToolKey(tool.key));
}

function UsageStatusCard({ usage }) {
  if (!usage) return null;

  const isUnlimited = Boolean(usage.is_unlimited || usage.cap === null);
  const used = Number(usage.used || 0);
  const cap = usage.cap === null ? null : Number(usage.cap || 0);
  const baseRemaining = Number(usage.remaining || 0);
  const addonRemaining = Number(usage.addon_credits?.remaining || 0);
  const totalAvailable = isUnlimited
    ? null
    : Number(usage.total_available ?? Math.max((cap || 0) - used, 0) + addonRemaining);

  return (
    <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-4" data-testid="tool-usage-summary">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">
        Current usage
      </p>

      {isUnlimited ? (
        <div className="mt-2">
          <p className="font-display text-2xl font-black">Unlimited</p>
          <p className="text-xs text-[#525252] mt-1">Your Pro plan does not need generation add-ons.</p>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-3 gap-3 text-center">
          <div className="bg-white border border-[#E5E7EB] p-3">
            <p className="font-display text-xl font-black">{used}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-[#737373]">Used</p>
          </div>
          <div className="bg-white border border-[#E5E7EB] p-3">
            <p className="font-display text-xl font-black">{baseRemaining}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-[#737373]">Plan left</p>
          </div>
          <div className="bg-white border border-[#E5E7EB] p-3">
            <p className="font-display text-xl font-black text-[#002FA7]">{addonRemaining}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-[#737373]">Add-ons</p>
          </div>
        </div>
      )}

      {!isUnlimited && (
        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-[#525252]">
          <span>Total available now: <strong className="text-[#0A0A0A]">{totalAvailable}</strong></span>
          {(usage.can_buy_addon || usage.addon_credits?.can_buy_addons) && (
            <Link to="/pricing" className="font-bold text-[#002FA7] hover:underline">Buy more</Link>
          )}
        </div>
      )}
    </div>
  );
}

export default function ToolPage() {
  const { tool: toolKey } = useParams();
  const navigate = useNavigate();
  const tool = useMemo(() => getToolByKey(toolKey), [toolKey]);
  const { usage, refreshUsage } = useAuth();

  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [view, setView] = useState("structured");

  useEffect(() => {
    if (!tool?.key) return;

    let cancelled = false;

    setInputs({});
    setResult(null);
    setView("structured");
    setHistoryLoading(true);

    const loadLatestHistory = async () => {
      const lookupKeys = getHistoryLookupKeys(tool.key);

      for (const lookupKey of lookupKeys) {
        try {
          const { data } = await axios.get(`${API}/history`, {
            params: {
              tool: lookupKey,
              limit: 1,
            },
            timeout: 30000,
          });

          if (cancelled) return;

          const latest = data?.items?.[0];
          if (!latest) continue;

          const normalized = normalizeGenerateResponse(
            latest,
            tool.key,
            latest.inputs || {}
          );

          if (!normalized.output || !String(normalized.output).trim()) {
            continue;
          }

          if (!sameToolKey(normalized.tool, tool.key)) {
            continue;
          }

          setInputs(normalized.inputs || {});
          setResult(normalized);
          return;
        } catch {
          // Try the next alias/fallback.
        }
      }

      // Final fallback: load recent history and find matching alias manually.
      try {
        const { data } = await axios.get(`${API}/history`, {
          params: {
            limit: 30,
          },
          timeout: 30000,
        });

        if (cancelled) return;

        const latest = (data?.items || []).find((item) =>
          sameToolKey(item.tool, tool.key)
        );

        if (!latest) return;

        const normalized = normalizeGenerateResponse(
          latest,
          tool.key,
          latest.inputs || {}
        );

        if (!normalized.output || !String(normalized.output).trim()) return;

        setInputs(normalized.inputs || {});
        setResult(normalized);
      } catch {
        // No history or not authenticated yet. Keep the default empty state.
      }
    };

    loadLatestHistory().finally(() => {
      if (!cancelled) setHistoryLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [tool?.key]);

  if (!tool) {
    return (
      <div className="container-wide py-24">
        <p className="font-mono text-sm text-[#737373]">Tool not found.</p>

        <Link
          to="/dashboard"
          className="btn-primary mt-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={16} weight="bold" /> Back to Studio
        </Link>
      </div>
    );
  }

  const Icon = tool.icon;

  const onSubmit = async (e) => {
    e.preventDefault();

    const missing = tool.fields.filter(
      (f) => f.required && !hasValue(inputs[f.name])
    );

    if (missing.length) {
      toast.error(`Please fill: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }

    setLoading(true);
    setView("structured");

    try {
      const { data } = await axios.post(
        `${API}/generate`,
        {
          tool: tool.key,
          inputs,
        },
        {
          timeout: 180000,
        }
      );

      const normalized = normalizeGenerateResponse(data, tool.key, inputs);

      if (!normalized.output || !String(normalized.output).trim()) {
        toast.error("The AI returned an empty result. Please try again.");
        return;
      }

      setResult(normalized);
      toast.success(
        normalized.addon_credit_used
          ? "Generated using one add-on credit and saved to History."
          : "Generated and saved to History."
      );
      await refreshUsage();
    } catch (err) {
      const data = err?.response?.data;
      const detail = data?.detail;

      if (detail?.error === "usage_cap" || data?.error === "usage_cap") {
        toast.error(
          "You've hit your monthly generation limit. Upgrade to continue."
        );
      } else if (detail?.error === "plan_gate" || data?.error === "plan_gate") {
        toast.error(
          detail?.message || data?.message || "This tool requires a paid plan."
        );
      } else if (err?.code === "ECONNABORTED") {
        toast.error("The AI took too long to respond. Please try again.");
      } else {
        toast.error(getApiErrorMessage(err));
      }

      await refreshUsage();
    } finally {
      setLoading(false);
    }
  };

  const visibleResult =
    result && sameToolKey(result.tool, tool.key) ? result : null;

  const resultInputs = visibleResult?.inputs || inputs;

  const copyResult = () => {
    if (!visibleResult?.output) return;

    navigator.clipboard.writeText(visibleResult.output);
    toast.success("Copied to clipboard.");
  };

  const printResult = () => {
    if (!visibleResult?.output) return;

    const previousTitle = document.title;
    const nextTitle = getPdfDocumentTitle(tool.name);
    let restored = false;

    const restoreTitle = () => {
      if (restored) return;
      restored = true;
      document.title = previousTitle;
      window.removeEventListener("afterprint", restoreTitle);
    };

    document.title = nextTitle;
    window.addEventListener("afterprint", restoreTitle);
    window.print();

    window.setTimeout(restoreTitle, 10000);
  };

  const relatedTools = TOOLS.filter(
    (t) => t.key !== tool.key && t.category === tool.category
  ).slice(0, 3);

  const hasCustomRenderer =
    tool.key === "ads" || tool.key === "social" || tool.key === "reviews";

  const currentPlan = normalizePlan(usage?.plan || "starter");
  const isGated = !userCanAccessTool(tool, usage);

  const isOverCap =
    usage &&
    usage.cap !== null &&
    !usage.is_unlimited &&
    Number(usage.total_available ?? Math.max(Number(usage.cap || 0) - Number(usage.used || 0), 0)) <= 0;

  if (isGated) {
    return (
      <div>
        <section className="border-b border-[#E5E7EB] bg-[#F5F5F5] print:hidden">
          <div className="container-wide py-8 md:py-10">
            <button
              onClick={() => navigate(-1)}
              className="font-mono text-xs uppercase tracking-[0.18em] text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft size={14} weight="bold" /> Back
            </button>
          </div>
        </section>

        <section className="container-wide py-10">
          <PlanGate tool={tool} plan={currentPlan} />
        </section>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-[#E5E7EB] bg-[#F5F5F5] print:hidden">
        <div className="container-wide py-8 md:py-10">
          <button
            onClick={() => navigate(-1)}
            className="font-mono text-xs uppercase tracking-[0.18em] text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft size={14} weight="bold" /> Back
          </button>

          <div className="mt-5 md:mt-6 flex items-start gap-4 md:gap-5">
            <div className="h-11 w-11 md:h-14 md:w-14 bg-[#002FA7] text-white flex items-center justify-center shrink-0">
              <Icon size={22} weight="duotone" className="md:hidden" />
              <Icon size={28} weight="duotone" className="hidden md:inline" />
            </div>

            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#737373]">
                {tool.category}
              </p>

              <div className="flex items-center gap-3 flex-wrap mt-1">
                <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter">
                  {tool.name}
                </h1>

                {tool.free ? (
                  <span
                    className="inline-flex items-center px-2 py-1 bg-[#002FA7] text-white font-mono text-[10px] font-bold uppercase tracking-[0.16em]"
                    data-testid="tool-plan-badge"
                  >
                    Free
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center px-2 py-1 border border-[#0A0A0A] text-[#0A0A0A] font-mono text-[10px] font-bold uppercase tracking-[0.16em]"
                    data-testid="tool-plan-badge"
                  >
                    Paid plan
                  </span>
                )}
              </div>

              <p className="mt-2 text-[#525252] text-base sm:text-lg max-w-xl">
                {tool.tagline}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-wide py-6 md:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
        <form
          onSubmit={onSubmit}
          className="lg:col-span-5 bg-white border border-[#E5E7EB] p-5 sm:p-7 flex flex-col gap-5 h-fit lg:sticky lg:top-24 print:hidden"
          data-testid="tool-form"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">
              Inputs
            </p>

            <h2 className="font-display text-xl font-bold">
              Tell the AI what you need.
            </h2>
          </div>

          <UsageStatusCard usage={usage} />

          {isOverCap && <UsageCapBanner usage={usage} />}

          {tool.fields.map((f) => (
            <div key={f.name} className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold">
                {f.label}{" "}
                {f.required && <span className="text-[#FF3B30]">*</span>}
              </label>

              {f.type === "textarea" ? (
                <textarea
                  className="input-line min-h-[120px] font-[inherit]"
                  placeholder={f.placeholder}
                  value={inputs[f.name] || ""}
                  onChange={(e) =>
                    setInputs({
                      ...inputs,
                      [f.name]: e.target.value,
                    })
                  }
                  data-testid={`input-${f.name}`}
                />
              ) : (
                <input
                  className="input-line"
                  type="text"
                  placeholder={f.placeholder}
                  value={inputs[f.name] || ""}
                  onChange={(e) =>
                    setInputs({
                      ...inputs,
                      [f.name]: e.target.value,
                    })
                  }
                  data-testid={`input-${f.name}`}
                />
              )}
            </div>
          ))}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary justify-center"
            data-testid="generate-btn"
          >
            {loading ? (
              <>
                <ArrowCounterClockwise
                  size={18}
                  weight="bold"
                  className="animate-spin"
                />{" "}
                Generating…
              </>
            ) : (
              <>
                <Lightning size={18} weight="bold" /> Generate
              </>
            )}
          </button>

          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#737373]">
            Saved to your history automatically
          </p>
        </form>

        <div className="lg:col-span-7 print-surface">
          {historyLoading && !visibleResult && !loading && (
            <div className="border border-[#E5E7EB] p-10 bg-white print:hidden">
              <div className="text-center mb-8">
                <ArrowCounterClockwise
                  size={24}
                  weight="bold"
                  className="animate-spin mx-auto text-[#002FA7]"
                />

                <p className="mt-4 font-display text-xl font-bold">
                  Loading your latest {tool.name} output…
                </p>
              </div>

              <div className="space-y-3">
                <div className="h-3 bg-[#F5F5F5] w-2/3 animate-pulse" />
                <div className="h-3 bg-[#F5F5F5] w-5/6 animate-pulse" />
                <div className="h-3 bg-[#F5F5F5] w-1/2 animate-pulse" />
              </div>
            </div>
          )}

          {!visibleResult && !loading && !historyLoading && (
            <div className="border border-dashed border-[#E5E7EB] bg-[#F5F5F5] p-12 text-center print:hidden">
              <Sparkle
                size={28}
                weight="duotone"
                className="mx-auto text-[#002FA7]"
              />

              <p className="mt-4 font-display text-xl font-bold">
                Your output will appear here.
              </p>

              <p className="mt-2 text-sm text-[#525252] max-w-md mx-auto">
                Fill in the inputs on the left and hit Generate. Results save
                to History automatically.
              </p>
            </div>
          )}

          {loading && visibleResult && (
            <div className="border border-[#BFDBFE] bg-[#EFF6FF] p-4 mb-4 text-sm text-[#1E40AF] print:hidden">
              <div className="flex items-center gap-2">
                <ArrowCounterClockwise
                  size={16}
                  weight="bold"
                  className="animate-spin"
                />
                <span className="font-semibold">
                  Generating a fresh output. Your previous saved result is still
                  shown below.
                </span>
              </div>
            </div>
          )}

          {loading && !visibleResult && (
            <div className="border border-[#E5E7EB] p-10 bg-white">
              <div className="text-center mb-8">
                <ArrowCounterClockwise
                  size={28}
                  weight="bold"
                  className="animate-spin mx-auto text-[#002FA7]"
                />

                <p className="mt-4 font-display text-xl font-bold">
                  Generating your {tool.name} output…
                </p>

                <p className="mt-2 text-sm text-[#525252] max-w-md mx-auto">
                  This can take a little longer for detailed SEO or marketing
                  outputs. Please keep this page open.
                </p>
              </div>

              <div className="space-y-3">
                <div className="h-3 bg-[#F5F5F5] w-2/3 animate-pulse" />
                <div className="h-3 bg-[#F5F5F5] w-5/6 animate-pulse" />
                <div className="h-3 bg-[#F5F5F5] w-1/2 animate-pulse" />
                <div className="h-3 bg-[#F5F5F5] w-3/4 animate-pulse" />
              </div>
            </div>
          )}

          {visibleResult && (
            <div
              className="border border-[#E5E7EB] bg-white"
              data-testid="tool-result"
              data-current-tool={tool.key}
            >
              <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-[#E5E7EB] flex-wrap print:hidden">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
                  Output · {new Date(visibleResult.created_at).toLocaleString()}
                </span>

                <div className="flex items-center gap-4">
                  {hasCustomRenderer && (
                    <div className="flex border border-[#E5E7EB]">
                      <button
                        type="button"
                        onClick={() => setView("structured")}
                        className={`px-2.5 py-1 text-xs font-semibold ${
                          view === "structured"
                            ? "bg-[#0A0A0A] text-white"
                            : "text-[#525252]"
                        }`}
                        data-testid="view-structured"
                      >
                        Structured
                      </button>

                      <button
                        type="button"
                        onClick={() => setView("markdown")}
                        className={`px-2.5 py-1 text-xs font-semibold ${
                          view === "markdown"
                            ? "bg-[#0A0A0A] text-white"
                            : "text-[#525252]"
                        }`}
                        data-testid="view-markdown"
                      >
                        Markdown
                      </button>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={printResult}
                    className="text-sm font-semibold text-[#0A0A0A] inline-flex items-center gap-1.5"
                    data-testid="print-result-btn"
                  >
                    <Printer size={14} weight="bold" /> PDF
                  </button>

                  <button
                    type="button"
                    onClick={copyResult}
                    className="text-sm font-semibold text-[#002FA7] inline-flex items-center gap-1.5"
                    data-testid="copy-result-btn"
                  >
                    <Copy size={14} weight="bold" /> Copy
                  </button>
                </div>
              </div>

              <div className="p-5 sm:p-7">
                <div className="hidden print:block mb-6 border-b border-[#0A0A0A] pb-4">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
                    Ordeneli · Marketing AI Studio
                  </p>

                  <h1 className="font-display text-2xl font-black mt-1">
                    {tool.name} output
                  </h1>

                  <p className="text-xs text-[#525252] mt-1">
                    {new Date(visibleResult.created_at).toLocaleString()}
                  </p>
                </div>

                {hasCustomRenderer && view === "structured" ? (
                  tool.key === "ads" ? (
                    <AdsRenderer
                      output={visibleResult.output}
                      inputs={resultInputs}
                    />
                  ) : tool.key === "social" ? (
                    <SocialRenderer
                      output={visibleResult.output}
                      inputs={resultInputs}
                    />
                  ) : (
                    <ReviewsRenderer
                      output={visibleResult.output}
                      inputs={resultInputs}
                    />
                  )
                ) : visibleResult.output ? (
                  <article
                    className="prose-tight text-[#0A0A0A] text-[15px] leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdown(visibleResult.output),
                    }}
                  />
                ) : (
                  <div className="border border-[#FDE68A] bg-[#FFFBEB] p-5 text-sm text-[#92400E] flex gap-3">
                    <WarningCircle size={20} weight="duotone" />
                    <p>
                      No output was returned. Please try again or refresh the
                      page.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {relatedTools.length > 0 && (
            <div className="mt-10 print:hidden">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373] mb-4">
                — Related tools
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedTools.map((rt) => {
                  const RIcon = rt.icon;

                  return (
                    <Link
                      key={rt.key}
                      to={`/tools/${rt.key}`}
                      className="border border-[#E5E7EB] p-4 hover:border-[#0A0A0A] transition-colors"
                      data-testid={`related-${rt.key}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-[#0A0A0A] text-white flex items-center justify-center">
                          <RIcon size={16} weight="duotone" />
                        </div>

                        <span className="font-display font-bold">
                          {rt.name}
                        </span>
                      </div>

                      <p className="text-xs text-[#525252] mt-2">
                        {rt.tagline}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}